import { Theme, useTheme } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import { CSSObject, Css, Cx, createMakeStyles } from 'tss-react';

type CustomVariant = Variant | 'body3' | 'subtitle3' | 'heading';

type AugmentTheme<TTheme extends Theme> = Omit<TTheme, 'typography'> & {
  typography: Record<CustomVariant, any> & Omit<TTheme['typography'], CustomVariant>;
};
type Result<TRuleName extends string, TTheme extends Theme = Theme> = {
  classes: Record<TRuleName, string>;
  theme: TTheme;
  css: Css;
  cx: Cx;
};

type StyleFunction<
  TTheme extends Theme,
  TProps,
  TClasses extends string = string,
  TRefs extends string = string,
> = (
  theme: AugmentTheme<TTheme>,
  props: TProps extends void ? {} : TProps,
  classes: Record<TRefs, string>,
) => Record<TClasses, CSSObject>;

export type CustomMakeStyles<TCustomTheme extends Theme = Theme> = {
  <TProps extends object | void = void, TClasses extends string = string>(
    cssObject: Record<TClasses, CSSObject> | StyleFunction<TCustomTheme, TProps, TClasses>,
  ): TProps extends void
    ? () => Result<TClasses, TCustomTheme>
    : (props: TProps) => Result<TClasses, TCustomTheme>;

  <TProps extends object | void = void, TRefs extends string = string>(
    name?: string,
  ): <TClasses extends string>(
    cssObject: Record<TClasses, CSSObject> | StyleFunction<TCustomTheme, TProps, TClasses, TRefs>,
  ) => TProps extends void
    ? () => Result<TClasses, TCustomTheme>
    : (props: TProps) => Result<TClasses, TCustomTheme>;
};

const { makeStyles: ourMakeStyles } = createMakeStyles({ useTheme });

const makeStyles = (defsOrName: any) => {
  if (typeof defsOrName === 'string' || defsOrName === undefined) {
    const maker = ourMakeStyles({ name: defsOrName });
    return maker;
  }
  const useStyles = ourMakeStyles()(defsOrName);
  return useStyles;
};

// we should never use this export directly(use on a per theme basis)
export default makeStyles as never;
